import * as React from "react";

import "./card-list-view.scss";

import {cx}                 from "emotion";
import {FontAwesomeIcon}    from "@fortawesome/react-fontawesome";
import {
    IconDefinition,
    faBan,
    faCartShopping,
    faDownload,
    faEllipsis,
    faFilePdf,
    faImage,
    faMagnifyingGlass,
    faPencil,
    faThumbsUp,
    faTriangleExclamation,
    faTruckFast,
}                           from "@fortawesome/free-solid-svg-icons";

import {AnyAction}                               from "@Common/react/any-action";
import {ComponentLink, ComponentNext, makeChain} from "@Common/react/cst2";
import {UseState}                                from "@Common/react/use-state";
import {Types}                                   from "@Common/types";
import {formatMoney}                             from "@Common/ui-components/language/format-money";

import {mapMouseEventAsPosition}        from "../../../common/mobile-card/dropdown/dropdown";
import {
    ATTRIBUTE_KEY_LABELS,
    ORDER_ATTRIBUTE_KEY_LABELS,
    ApprovalRequestAttribute,
    ApprovalRequestAttributesDict,
    ApprovalRequestAttributesService,
    ApprovalRequestOrderAttribute,
    ApprovalRequestOrderAttributesDict,
}                                       from "../approval-request-attributes";
import {ApprovalRequest}                from "../approval-requests";

export const ApprovalRequestsCardListView = ({approvalRequestEntries, attributesService}:{
    approvalRequestEntries  : Array<{approval: ApprovalRequest, attributes: ApprovalRequestAttributesDict}>,
    attributesService       : ComponentNext<typeof ApprovalRequestAttributesService, "renderChildren">,
}) => (
    approvalRequestEntries.map(({approval, attributes}) => <ApprovalRequestCard {...{approval, attributes, attributesService}}/>)
);

const ApprovalRequestCard = ({approval, attributes, attributesService}:{
    approval            : ApprovalRequest,
    attributes          : ApprovalRequestAttributesDict,
    attributesService   : ComponentNext<typeof ApprovalRequestAttributesService, "renderChildren">,
}) => (
    makeChain<JSX.Element>()
    .then("expandedState", ({}, next:ComponentLink<typeof UseState<boolean>>) => UseState({next, initValue: false}))
    .then("menuState", ({}, next:ComponentLink<typeof UseState<{right: number, top: number}>>) => UseState({next, initValue: null}))
    .then(({expandedState: {onChange: setExpanded, value: expanded}, menuState: {onChange: setMenuPosition, value: menuPosition}}) => (
        <article className="approval-request-card-2888f" xd-qa-type="card" xd-qa-name={approval.approvalGate + " " +  approval.id}>
            <header>
                <div className="titles">
                    <span className="title" xd-qa-type="label" xd-qa-name="Title">{attributes.corrupted ? `[CORRUPTED] ${approval.id}` : `${attributes.id?.label} ${attributes.id?.value}`}</span>
                    <span className="subtitle" xd-qa-type="label" xd-qa-name="Date">{formatDate(attributes.date?.value)}</span>
                </div>
                <aside>
                    <ul className="details">
                        {attributes.price && (
                            <li className="strong">
                                <span xd-qa-type="label" xd-qa-name="Total">{formatMoney(attributes.price.value, {useFreeLabel: false, alwaysShowDollarSymbol: true})}</span>
                            </li>
                        )}
                        {attributes.corrupted ? (
                            <li xd-qa-type="icon" xd-qa-name="Corrupted Order">
                                <button
                                    onClick={() => {
                                        window.alert(attributes.corrupted.message || "This approval request is corrupted and cannot be approved");
                                    }}
                                    className="icon"
                                ><FontAwesomeIcon icon={faTriangleExclamation}/></button>
                            </li>
                        ) : attributes.approve && (
                            <li xd-qa-type="button" xd-qa-name="Actions">
                                <button
                                    onClick={(e) => {
                                        const position = mapMouseEventAsPosition(e);
                                        window.setTimeout(() => setMenuPosition(position), 0);
                                    }}
                                    className="icon"
                                ><FontAwesomeIcon icon={faEllipsis}/></button>
                            </li>
                        )}
                    </ul>
                </aside>
            </header>
            <main>
                <ul className="details">
                    {APPROVAL_DETAILS_KEYS.filter(key => key in attributes).map(
                        key => {
                            const attribute = attributes[key];
                            let content:React.ReactNode;
                            if (attribute.key === "agent") {
                                content = attribute.isBranchOrder ? "Branch Order" : attribute.value.name;
                            } else if (attribute.key === "branch") {
                                content = attribute.value.name;
                            } else if ("value" in attribute && typeof attribute.value !== "object") {
                                content = attribute.value;
                            } else if (attribute.key === "hasFastShipping") {
                                content = <FontAwesomeIcon icon={faTruckFast}/>;
                            } else if (attribute.key === "hasPromoCode") {
                                content = attribute.code;
                            }
                            return (
                                <li key={key}>
                                    <span className="label" xd-qa-type="label" xd-qa-name={ATTRIBUTE_KEY_LABELS[key]}>{ATTRIBUTE_KEY_LABELS[key]}</span>
                                    <span xd-qa-type="text" xd-qa-name={ATTRIBUTE_KEY_LABELS[key]}>{content}</span>
                                </li>
                            );
                        }
                    )}
                </ul>
                {!attributes.corrupted && <aside>
                    <a onClick={() => setExpanded(!expanded)}
                        xd-qa-type="link"
                        xd-qa-name={expanded ? "- Less" : "+ More"}
                    >
                        {expanded ? "- Less" : "+ More"}
                    </a>
                </aside>}
                {expanded && (
                    <section className="orders">
                        {(approval.orders as Array<Types.Utils.ArrayItem<ApprovalRequest['orders']>>).map(order => (
                            OrderCard({order, attributes: attributesService.makeOrderAttributes(approval, order)})
                        ))}
                    </section>
                )}
            </main>
            {menuPosition && (
                ApprovalActionsMenu({approval, attributes, position: menuPosition, onClose: () => setMenuPosition(null)})
            )}
        </article>
    ))
    .run()
);

const APPROVAL_ACTIONS_ORDERED:ApprovalRequestAttribute['key'][] = [
    "goToCart",
    "deny",
    "approve",
];

const APPROVAL_ACTIONS_ICONS:{[Key in Extract<ApprovalRequestAttribute, {run?: () => any}>['key']]: IconDefinition} = {
    approve     : faThumbsUp,
    deny        : faBan,
    goToCart    : faCartShopping,
};

const ApprovalActionsMenu = ({attributes, position, onClose: emitClose}:{
    approval    : ApprovalRequest,
    attributes  : ApprovalRequestOrderAttributesDict,
    position    : {top: number, right: number},
    onClose     : () => void,
}) => (
    <div className="dropdown" style={position}>
        <AnyAction action={emitClose}/>
        {APPROVAL_ACTIONS_ORDERED.filter(key => key in attributes).map((key) => (
            <a
                key={key}
                className={cx("dropdown-item", key)}
                xd-qa-type="dropdown_item"
                xd-qa-name={ATTRIBUTE_KEY_LABELS[key]}
                onClick={attributes[key].run}
            >
                {key !== "approve" && <i className="icon"><FontAwesomeIcon icon={APPROVAL_ACTIONS_ICONS[key]}/></i>}
                <span className="label" xd-qa-type="label" xd-qa-name={ATTRIBUTE_KEY_LABELS[key]}>{ATTRIBUTE_KEY_LABELS[key]}</span>
            </a>
        ))}
    </div>
);

const OrderCard = ({attributes, order}:{
    attributes  : ApprovalRequestOrderAttributesDict,
    order       : Types.Utils.ArrayItem<ApprovalRequest['orders']>,
}) => (
    makeChain<JSX.Element>()
    .then("menuState", ({}, next:ComponentLink<typeof UseState<{right: number, top: number}>>) => UseState({next, initValue: null}))
    .then(({menuState: {onChange: setMenuPosition, value: menuPosition}}) => (
        <article xd-qa-type="card" xd-qa-name={order.idOrder}>
            <header>
                <div className="titles">
                    <span className="title" xd-qa-type="text" xd-qa-name="Order Name">{order.orderName}</span>
                    <span className="subtitle" xd-qa-type="text" xd-qa-name="Order ID">{order.idOrder}</span>
                </div>
                <aside>
                    <ul className="details">
                        {"quantity" in order && <li>
                            <span className="label" xd-qa-type="label" xd-qa-name="QTY">Qty</span>
                            <span xd-qa-type="text" xd-qa-name="Qty">{order.quantity}</span>
                        </li>}
                        {attributes.price && (
                            <li className="strong">{formatMoney(attributes.price.value)}</li>
                        )}
                        <li xd-qa-type="button" xd-qa-name="Actions">
                            <button
                                onClick={(e) => {
                                    const position = mapMouseEventAsPosition(e);
                                    window.setTimeout(() => setMenuPosition(position), 0);
                                }}
                                className="icon"
                            ><FontAwesomeIcon icon={faEllipsis}/></button>
                        </li>
                    </ul>
                </aside>
            </header>
            {!attributes.corrupted && (
                <main>
                    <ul className="details">
                        <li>
                            <span className="label" xd-qa-type="label" xd-qa-name="Type">Type</span>
                            <span xd-qa-type="text" xd-qa-name="Type">{order.orderType}</span>
                        </li>
                        <li>
                            <span className="label" xd-qa-type="label" xd-qa-name="Category">Category</span>
                            <span xd-qa-type="text" xd-qa-name="Category">{order.category?.name}</span>
                        </li>
                        <li>
                            <span className="label" xd-qa-type="label" xd-qa-name="Subcategory">Subcategory</span>
                            <span xd-qa-type="text" xd-qa-name="Subcategory">{order.subCategory?.name}</span>
                        </li>
                        <li>
                            <span className="label" xd-qa-type="label" xd-qa-name="Style">Style</span>
                            <span xd-qa-type="text" xd-qa-name="Style">{order.style?.name}</span>
                        </li>
                        <li>
                            <span className="label" xd-qa-type="label" xd-qa-name="Template">Template</span>
                            <span xd-qa-type="text" xd-qa-name="Template">{order.templateName}</span>
                        </li>
                    </ul>
                </main>
            )}
            {menuPosition && (
                <OrderActionsMenu attributes={attributes} order={order} position={menuPosition} onClose={() => setMenuPosition(null)}/>
            )}
        </article>
    ))
    .run()
);

const ORDER_ACTIONS_ICONS:{[Key in ApprovalRequestOrderAttribute['key']]?: IconDefinition} = {
    deny            : faBan,
    downloadPDF     : faFilePdf,
    downloadProof   : faDownload,
    edit            : faPencil,
    previewProof    : faImage,
    showDetails     : faMagnifyingGlass,
};

const OrderActionsMenu = ({attributes, position, onClose: emitClose}:{
    attributes  : ApprovalRequestOrderAttributesDict,
    order       : Types.Utils.ArrayItem<ApprovalRequest['orders']>,
    position    : {right: number, top: number},
    onClose     : () => void,
}) => (
    <div className="dropdown" style={position}>
        <AnyAction action={emitClose}/>
        {Object.keys(ORDER_ACTIONS_ICONS).filter(key => key in attributes).map((key) => (
            <a
                key={key}
                className="dropdown-item"
                xd-qa-type="dropdown_item"
                xd-qa-name={ORDER_ATTRIBUTE_KEY_LABELS[key]}
                onClick={attributes[key].run}
            >
                <i className="icon"><FontAwesomeIcon icon={ORDER_ACTIONS_ICONS[key]}/></i>
                {ORDER_ATTRIBUTE_KEY_LABELS[key]}
            </a>
        ))}
    </div>
);

const APPROVAL_DETAILS_KEYS:ApprovalRequestAttribute['key'][] = [
    "branch",
    "agent",
    "email",
    "delivery",
    "hasFastShipping",
    "hasPromoCode",
];

const formatDate = (() => {
    const formatter = new Intl.DateTimeFormat("en-US", {dateStyle: "medium"});
    return formatter.format;
})();

